// Skills/Templates API Service
// Handles content generation templates (built-in and user-defined)

import { API_BASE_URL } from '../config/api';

/**
 * Get all available skills (built-in + user templates)
 * @param {string} token - Auth token
 * @returns {Promise<Object>} - Skills list grouped by category
 */
export const getSkills = async (token) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/skills/`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || '获取模板列表失败');
    }

    return response.json();
};

/**
 * Get template assets (meta prompt encapsulated)
 * @param {string} token
 * @returns {Promise<Array>}
 */
export const getTemplateAssets = async (token) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/skills/assets/`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || '获取模板资产失败');
    }

    return response.json();
};

/**
 * Create template asset
 * @param {Object} asset
 * @param {string} token
 * @returns {Promise<Object>}
 */
export const createTemplateAsset = async (asset, token) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/skills/assets/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(asset)
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || '创建模板资产失败');
    }

    return response.json();

};

/**
 * Update template asset
 * @param {number} assetId
 * @param {Object} payload
 * @param {string} token
 * @returns {Promise<Object>}
 */
export const updateTemplateAsset = async (assetId, payload, token) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/skills/assets/${assetId}/`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || '更新模板资产失败');
    }

    return response.json();
};

/**
 * Delete template asset (soft delete)
 * @param {number} assetId
 * @param {string} token
 * @returns {Promise<void>}
 */
export const deleteTemplateAsset = async (assetId, token) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/skills/assets/${assetId}/`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || '删除模板资产失败');
    }
};

/**
 * Apply template asset for generation
 * @param {number} assetId
 * @param {string} token
 * @returns {Promise<{skill_name: string, asset_id: number}>}
 */
export const applyTemplateAsset = async (assetId, token) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/skills/assets/${assetId}/apply/`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || '应用模板资产失败');
    }

    return response.json();
};

/**
 * Get a specific skill by ID
 * @param {string} skillId - Skill ID
 * @param {string} token - Auth token
 * @returns {Promise<Object>} - Skill details
 */
export const getSkillById = async (skillId, token) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/skills/${skillId}/`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || '获取模板失败');
    }

    return response.json();
};

/**
 * Create a new user template
 * @param {Object} template - Template data
 * @param {string} token - Auth token
 * @returns {Promise<Object>} - Created template
 */
export const createUserTemplate = async (template, token) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/skills/templates/create/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(template)
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || '创建模板失败');
    }

    return response.json();
};

/**
 * Update a user template
 * @param {string} templateId - Template ID
 * @param {Object} template - Updated template data
 * @param {string} token - Auth token
 * @returns {Promise<Object>} - Updated template
 */
export const updateUserTemplate = async (templateId, template, token) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/skills/templates/${templateId}/`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(template)
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || '更新模板失败');
    }

    return response.json();
};

/**
 * Delete a user template
 * @param {string} templateId - Template ID
 * @param {string} token - Auth token
 * @returns {Promise<Object>} - Deletion result
 */
export const deleteUserTemplate = async (templateId, token) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/skills/templates/${templateId}/delete/`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || '删除模板失败');
    }

    return response.json();
};

/**
 * Preview template with sample variables
 * @param {Object} template - Template to preview
 * @param {Object} variables - Sample variable values
 * @param {string} token - Auth token
 * @returns {Promise<Object>} - Rendered preview
 */
export const previewTemplate = async (template, variables, token) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/skills/preview/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ template, variables })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || '预览失败');
    }

    return response.json();
};

// Built-in skill categories
export const SKILL_CATEGORIES = {
    xiaohongshu: {
        name: '小红书',
        icon: '📕',
        skills: [
            { id: 'xiaohongshu_default', name: '通用笔记', description: '适合各类日常分享' },
            { id: 'xiaohongshu_seeding', name: '种草文', description: '产品推荐、好物分享' },
            { id: 'xiaohongshu_review', name: '测评文', description: '产品测评、对比分析' },
            { id: 'xiaohongshu_story', name: '故事文', description: '个人经历、情感分享' },
          { id: 'xiaohongshu_亲子', name: '亲子内容', description: '宝妈旅行社招商内容' },
          { id: 'xiaohongshu_洗发水', name: '洗发水营销', description: '草本洗发水爆款内容' },
        ]
    },
    wechat: {
        name: '公众号',
        icon: '💬',
        skills: [
            { id: 'wechat_marketing', name: '营销文', description: '品牌推广、活动宣传' },
            { id: 'wechat_tutorial', name: '教程文', description: '知识科普、操作指南' }
        ]
    },
    product_image: {
        name: '产品海报',
        icon: '🎨',
        skills: [
            { id: 'product_image_generation_pro', name: '产品视觉渲染', description: '智能硬件/SaaS界面/概念生态，影棚级光影构图' }
        ]
    }
};

const skillService = {
    getSkills,
    getSkillById,
    createUserTemplate,
    updateUserTemplate,
    deleteUserTemplate,
    previewTemplate,
    getTemplateAssets,
    createTemplateAsset,
    updateTemplateAsset,
    deleteTemplateAsset,
    applyTemplateAsset,
    SKILL_CATEGORIES
};

export default skillService;
